/**
 * Iter-229 — Booking Details V2.
 *
 * Per user spec: prominently displays Dress Code, Grooming Requirements,
 * Sent-home Warnings, Event Instructions, Parking Info, Event Contacts,
 * Required Documents, Certifications, and Reporting Requirements.
 *
 * Backed by the existing GET /api/talent/bookings/{id} response — any field
 * missing from the payload simply renders a "Not provided yet" placeholder
 * so the section is still discoverable.
 *
 * Includes a Tasks tab with the 13-item checklist requested in the spec.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { talentAPI } from '../../utils/api';
import api from '../../utils/api';
import { Button } from '../ui/button';
import {
  ChevronLeft, AlertTriangle, Shirt, UserCheck, Info, Car, Phone, MessageSquare,
  CheckCircle2, CircleAlert, Play, LayoutGrid, FileText, ListChecks, Calendar,
  Loader2, Image as ImageIcon, ClipboardCheck, Receipt, Check, Circle,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatTimeRange } from '../../utils/timeFormat';
import BookingActionsSheet from './BookingActionsSheet';
import GuidedCompletionFlow from './GuidedCompletionFlow';
import JobPhotoUpload from '../JobPhotoUpload';
import TalentExpensesPanel from '../TalentExpensesPanel';
import { PostEventRecapModal } from '../PostEventRecapModal';
import TalentTaskCard from '../tasks/TalentTaskCard';

const SectionRow = ({ icon: Icon, title, anchor, children }) => (
  <div id={anchor} className="border-t border-slate-100 first:border-t-0 pt-3 first:pt-0 mt-3 first:mt-0">
    <div className="flex items-center gap-2 mb-1.5">
      <span className="w-8 h-8 rounded-lg bg-cyan-50 text-cyan-700 grid place-items-center">
        <Icon className="w-4 h-4" />
      </span>
      <span className="text-sm font-semibold text-slate-900">{title}</span>
    </div>
    <div className="pl-10 text-xs text-slate-700 leading-relaxed">{children}</div>
  </div>
);

const NotProvided = ({ what }) => (
  <span className="italic text-slate-400">{what} not provided by client yet.</span>
);

const Pill = ({ tone = 'slate', children, testid }) => (
  <span
    data-testid={testid}
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-${tone}-50 text-${tone}-700 border border-${tone}-200`}
  >
    {children}
  </span>
);

/* ----------------------- task checklist (presentation-only) -------------- */

function buildTasks(booking) {
  const acks = booking?.task_acknowledgements || {};
  const done = (k) => !!acks[k];
  return [
    { key: 'confirm_booking',  label: 'Confirm booking',              done: booking?.status === 'confirmed' || done('confirm_booking') },
    { key: 'review_details',   label: 'Review event details',         done: done('review_details') },
    { key: 'review_instructions', label: 'Review instructions',       done: done('review_instructions') },
    { key: 'review_dress',     label: 'Review dress code',            done: done('review_dress') },
    { key: 'review_grooming',  label: 'Review grooming requirements', done: done('review_grooming') },
    { key: 'review_documents', label: 'Review documents',             done: done('review_documents') },
    { key: 'check_in',         label: 'Check in',                     done: !!booking?.clock_in_time },
    { key: 'check_out',        label: 'Check out',                    done: !!booking?.clock_out_time },
    { key: 'photos',           label: 'Submit photos',                done: !!booking?.photos_submitted_at },
    { key: 'recap',            label: 'Submit recap',                 done: !!booking?.recap_submitted_at },
    { key: 'reimbursements',   label: 'Submit reimbursements',        done: !!booking?.reimbursements_submitted_at },
  ];
}

const BookingDetailsV2 = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialTab = params.get('tab') || 'details';
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(initialTab);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [recapOpen, setRecapOpen] = useState(false);
  const [stateInfo, setStateInfo] = useState(null);
  const [forceDetails, setForceDetails] = useState(false);
  const [hasApprovedTasks, setHasApprovedTasks] = useState(false);

  // Post-shift detection drives the guided completion flow. We rely on the
  // backend attendance state (single source of truth) — NOT clock records —
  // so a shift admin-marked worked/verified still shows the flow even if the
  // talent could never clock in.
  useEffect(() => {
    let m = true;
    api.get(`/payment-readiness/booking/${id}/state`)
      .then(r => { if (m) setStateInfo(r.data); })
      .catch(() => {});
    return () => { m = false; };
  }, [id]);

  // Iter-232b — Re-sync tab + auto-open recap modal when the URL changes
  // (e.g. user navigates from the Actions sheet to `?tab=recap`).
  useEffect(() => {
    const t = params.get('tab') || 'details';
    setTab(t);
    if (t === 'recap') setRecapOpen(true);
  }, [params]);

  // Iter-232b — Smooth-scroll to anchor (Instructions / Dress / Grooming)
  // once the details tab finishes rendering.
  useEffect(() => {
    if (tab !== 'details' || loading) return;
    const hash = window.location.hash?.replace('#', '');
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) {
      // Wait one paint so layout is settled.
      requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  }, [tab, loading, booking]);

  useEffect(() => {
    let m = true;
    setLoading(true);
    talentAPI.getBookings()
      .then(async r => {
        if (!m) return;
        const found = (r.data || []).find(b => b.id === id) || null;
        if (found) { setBooking(found); return; }
        // Post-event bookings (completed / paid) are excluded from the
        // upcoming-bookings list, so fall back to the single-booking
        // endpoint (works for any status) — otherwise the completion
        // deep-link would show "Booking not found".
        try {
          const one = await api.get(`/talent/bookings/${id}`);
          if (m && one.data) {
            setBooking({
              ...one.data,
              event_title: one.data.event_title || one.data.venue_name,
              client_name: one.data.company_name || one.data.client_name,
            });
          }
        } catch (e2) { /* stays null → not-found UI */ }
      })
      .catch(err => { console.error('Failed to load booking', err); })
      .finally(() => { if (m) setLoading(false); });
    return () => { m = false; };
  }, [id]);

  const tasks = useMemo(() => buildTasks(booking), [booking]);
  const tasksDone = tasks.filter(t => t.done).length;
  const tasksPct = tasks.length ? Math.round((tasksDone / tasks.length) * 100) : 0;

  const det = useMemo(() => {
    // Iter-229 — read briefing fields. Falls back to scalar columns and
    // top-level booking fields if event_briefing is missing/empty.
    // Iter-234 — Surface the dress code preset (name + image + description
    // + checklist) and grooming brief that the CLIENT selected at job
    // creation. Falls back to legacy free-text fields when the preset
    // wasn't used.
    const eb = booking?.event_briefing || {};
    const groomingList = Array.isArray(booking?.grooming_requirements)
      ? booking.grooming_requirements
      : [];
    return {
      dress_code:           booking?.dress_code || eb.dress_code || '',
      dress_code_name:      booking?.dress_code_name || '',
      dress_code_description: booking?.dress_code_description || '',
      dress_code_images:    Array.isArray(booking?.dress_code_images) ? booking.dress_code_images : [],
      dress_code_checklist: Array.isArray(booking?.dress_code_checklist) ? booking.dress_code_checklist : [],
      attire_restrictions:  Array.isArray(booking?.attire_restrictions) ? booking.attire_restrictions : [],
      grooming_list:        groomingList,
      grooming_images:      Array.isArray(booking?.grooming_images) ? booking.grooming_images : [],
      grooming_requirements: eb.grooming || '',
      // 2026-06 FIX — the admin's full duties blob is the primary instructions
      // source; fall back to structured briefing only when it's empty.
      duties:               booking?.duties || '',
      event_instructions:   eb.event_instructions || booking?.event_instructions || booking?.shift_description || '',
      arrival_instructions: booking?.arrival_instructions || '',
      setup_instructions:   booking?.setup_instructions || '',
      entrance_instructions: booking?.entrance_instructions || '',
      onsite_directions:    booking?.onsite_directions || '',
      parking_info:         eb.parking_info || booking?.parking_instructions || '',
      sent_home_policy:     eb.sent_home_policy || '',
      contact_name:         eb.contact_name || '',
      contact_role:         eb.contact_role || '',
      contact_phone:        eb.contact_phone || '',
      contact_email:        eb.contact_email || '',
      contact_instructions: booking?.contact_instructions || '',
      reporting_instructions: eb.reporting_instructions || booking?.reporting_location || '',
      recap_required:       !!booking?.recap_required,
      recap_photo_min:      booking?.recap_photo_min || null,
      recap_required_shots: Array.isArray(booking?.recap_required_shots) ? booking.recap_required_shots : [],
      important_notes:      booking?.important_notes || '',
      required_documents:   (eb.required_documents || '').split(',').map(s => s.trim()).filter(Boolean),
      certifications:       (eb.certifications || '').split(',').map(s => s.trim()).filter(Boolean),
    };
  }, [booking]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>;
  }
  if (!booking) {
    return (
      <div className="min-h-screen grid place-items-center p-8 text-center">
        <div>
          <p className="text-slate-700">Booking not found.</p>
          <Button onClick={() => navigate('/talent/bookings-v2')} className="mt-4">Back</Button>
        </div>
      </div>
    );
  }

  const POST_SHIFT_STATES = new Set([
    'awaiting_clock_out', 'awaiting_talent_action', 'awaiting_admin_approval',
    'ready_for_payment', 'paid', 'no_show_or_awaiting_action', 'attendance_verification_needed',
  ]);
  const wantComplete = params.get('complete') === '1';
  const isPostShift = !!stateInfo && (
    POST_SHIFT_STATES.has(stateInfo.overall_status) ||
    ['worked', 'verified'].includes(stateInfo.attendance_status)
  );
  const guided = (wantComplete || isPostShift) && !forceDetails;

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="booking-details-v2">
      {/* Cover header */}
      <div className="relative h-44 bg-gradient-to-br from-slate-900 via-cyan-900 to-cyan-700 px-4 pb-4 flex flex-col justify-end text-white">
        <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="bg-white/10 backdrop-blur rounded-full w-9 h-9 grid place-items-center" data-testid="back-btn">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={() => setActionsOpen(true)} className="bg-white/10 backdrop-blur rounded-full px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5" data-testid="header-actions">
            <LayoutGrid className="w-3.5 h-3.5" /> Actions
          </button>
        </div>
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Pill tone="emerald">● {booking.is_date_tbd ? 'Date: TBD' : (booking.shift_date ? formatDate(booking.shift_date) : 'Date: TBD')}</Pill>
          <Pill tone="cyan">{booking.status || 'confirmed'}</Pill>
          {/* 2026-08-03 P0 — talent reported the shift start/end time
              was missing across every booking view. Surface it here in
              the hero AND in the info grid below so it's impossible
              to miss regardless of scroll position. */}
          {(!booking.is_time_tbd && (booking.start_time || booking.end_time)) ? (
            <Pill tone="amber" testid="hero-shift-time">
              🕒 {formatTimeRange(booking.start_time, booking.end_time)}
            </Pill>
          ) : (
            <Pill tone="amber" testid="hero-shift-time">🕒 Time: TBD</Pill>
          )}
        </div>
        <h2 className="text-2xl font-bold leading-tight">{booking.event_title || 'Event'}</h2>
        <div className="text-white/80 text-sm" data-testid="hero-subhead">
          {booking.gig_role_name || booking.role_name || ''} · {booking.is_date_tbd ? 'Date: TBD' : (booking.shift_date ? formatDate(booking.shift_date) : 'Date: TBD')}
          {(!booking.is_time_tbd && (booking.start_time || booking.end_time)) && (
            <> · <span className="font-semibold" data-testid="hero-time-inline">{formatTimeRange(booking.start_time, booking.end_time)}</span></>
          )}
          {booking.hourly_rate ? ` · $${booking.hourly_rate}/hr` : ''}
        </div>
      </div>

      {guided && (
        <div className="px-4 pt-4">
          <GuidedCompletionFlow booking={booking} onViewDetails={() => setForceDetails(true)} />
        </div>
      )}

      {!guided && (
      <>
      <div className="px-4 pt-3 pb-24 space-y-3">
        {/* Tabs */}
        <div className="bg-white rounded-xl border border-slate-200 p-1 flex flex-wrap text-xs font-semibold shadow-sm overflow-x-auto" data-testid="booking-tabs">
          {[
            { k: 'details',        l: 'Details' },
            { k: 'tasks',          l: hasApprovedTasks ? 'Tasks' : `Tasks (${tasksDone}/${tasks.length})` },
            { k: 'photos',         l: 'Photos' },
            { k: 'recap',          l: 'Recap' },
            { k: 'reimbursements', l: 'Reimbursements' },
            { k: 'shifts',         l: 'Shifts' },
          ].map(t => (
            <button key={t.k}
                    onClick={() => setTab(t.k)}
                    className={`flex-1 min-w-fit py-1.5 px-2 rounded-lg whitespace-nowrap ${tab === t.k ? 'bg-cyan-600 text-white' : 'text-slate-600'}`}
                    data-testid={`tab-${t.k}`}>
              {t.l}
            </button>
          ))}
        </div>

        {tab === 'details' && (
          <>
            {/* Sent-home warnings — always prominent if present */}
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 flex gap-2" data-testid="sent-home-warning">
              <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-rose-800">Sent-home warnings</div>
                <p className="text-xs text-rose-700">{det.sent_home_policy || 'Late arrival, missing required uniform/dress code, or inappropriate grooming may result in being sent home without pay.'}</p>
              </div>
            </div>

            {/* 2026-08-03 P0 — dedicated "When" card so shift start / end
                time is unmissable. Talent reported not seeing the time on
                any Booking Details view. Now surfaced 3× (hero pill,
                hero subhead, dedicated card). */}
            <div className="bg-white rounded-xl border border-slate-200 p-3" data-testid="event-when-card">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-cyan-50 text-cyan-700 grid place-items-center shrink-0">
                  <Calendar className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">When</div>
                  <div className="text-base font-bold text-slate-900" data-testid="when-date">
                    {booking.is_date_tbd ? 'Date: TBD' : (booking.shift_date ? formatDate(booking.shift_date) : 'Date: TBD')}
                  </div>
                  <div className="text-sm text-slate-700 mt-0.5" data-testid="when-time">
                    {(!booking.is_time_tbd && (booking.start_time || booking.end_time))
                      ? formatTimeRange(booking.start_time, booking.end_time)
                      : 'Time: TBD'}
                  </div>
                  {booking.timezone && (
                    <div className="text-[11px] text-slate-500 mt-0.5" data-testid="when-tz">
                      Times shown in venue timezone ({booking.timezone})
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 2026-06 FIX — Job duties & responsibilities. The admin's full
                instructions live in `duties`; this card was previously absent
                from BookingDetailsV2 so assigned talent never saw them. */}
            {det.duties && (
              <div className="bg-white rounded-xl border border-slate-200 p-3" data-testid="job-duties-card">
                <SectionRow icon={ListChecks} title="Job duties & responsibilities" anchor="duties">
                  <p className="whitespace-pre-wrap text-xs text-slate-700 leading-relaxed">{det.duties}</p>
                </SectionRow>
              </div>
            )}

            {/* AI-organized requirements (creator-approved). Additive — original
                duties above stays intact. */}
            {booking.organized_requirements && (() => {
              const o = booking.organized_requirements;
              const labels = {
                event_briefing: 'Event instructions & client briefing',
                performance_expectations: 'Performance expectations',
                reporting_requirements: 'Reporting requirements',
                required_photos: 'Required photos & documentation',
                dress_code: 'Dress code & grooming', parking_logistics: 'Parking & logistics',
              };
              const text = Object.keys(labels).filter((k) => o[k] && String(o[k]).trim());
              const tasks = Array.isArray(o.task_checklist) ? o.task_checklist.filter(Boolean) : [];
              if (!text.length && !tasks.length) return null;
              return (
                <div className="bg-white rounded-xl border border-fuchsia-200 p-3" data-testid="organized-requirements-card">
                  <h3 className="text-sm font-semibold text-fuchsia-800 mb-2 flex items-center gap-1.5">
                    <ClipboardCheck className="w-4 h-4" /> Job requirements
                  </h3>
                  <div className="space-y-2 text-xs text-slate-700">
                    {text.map((k) => (
                      <div key={k}><span className="font-semibold text-slate-800">{labels[k]}: </span><span className="whitespace-pre-wrap">{o[k]}</span></div>
                    ))}
                    {tasks.length > 0 && (
                      <div>
                        <span className="font-semibold text-slate-800">Task checklist:</span>
                        <ul className="list-disc list-inside mt-0.5">{tasks.map((t, i) => <li key={i}>{t}</li>)}</ul>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            <div className="bg-white rounded-xl border border-slate-200 p-3" data-testid="event-info-card">
              <SectionRow icon={Shirt} title="Dress code" anchor="dress">
                {/* Iter-234 — show client-selected dress code preset:
                    name, image(s), description, checklist, and attire
                    restrictions. Falls back to plain-text dress_code. */}
                {(det.dress_code_name || det.dress_code_description || det.dress_code_images.length > 0 || det.dress_code_checklist.length > 0 || det.dress_code) ? (
                  <div className="space-y-2">
                    {det.dress_code_name && (
                      <div className="text-sm font-semibold text-slate-900" data-testid="dress-code-name">
                        {det.dress_code_name}
                      </div>
                    )}
                    {det.dress_code_images.length > 0 && (
                      <div className="flex flex-wrap gap-2" data-testid="dress-code-images">
                        {det.dress_code_images.map((src, i) => (
                          <a
                            key={i}
                            href={src}
                            target="_blank"
                            rel="noreferrer"
                            className="block w-24 h-24 rounded-lg overflow-hidden border border-slate-200 bg-slate-50"
                          >
                            <img
                              src={src}
                              alt={`Dress code reference ${i + 1}`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </a>
                        ))}
                      </div>
                    )}
                    {det.dress_code_description && (
                      <p className="text-xs text-slate-700 whitespace-pre-line" data-testid="dress-code-description">
                        {det.dress_code_description}
                      </p>
                    )}
                    {det.dress_code_checklist.length > 0 && (
                      <ul className="text-xs text-slate-700 space-y-1 pl-1" data-testid="dress-code-checklist">
                        {det.dress_code_checklist.map((item, i) => (
                          <li key={i} className="flex items-start gap-1.5">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {det.attire_restrictions.length > 0 && (
                      <div className="pt-1" data-testid="attire-restrictions">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-rose-700 mb-1">Restrictions</div>
                        <ul className="text-xs text-rose-700 space-y-0.5 pl-1">
                          {det.attire_restrictions.map((item, i) => (
                            <li key={i} className="flex items-start gap-1.5">
                              <CircleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {!det.dress_code_name && det.dress_code && (
                      <p className="text-xs text-slate-700">{det.dress_code}</p>
                    )}
                  </div>
                ) : (
                  <NotProvided what="Dress code" />
                )}
              </SectionRow>
              <SectionRow icon={UserCheck} title="Grooming requirements" anchor="grooming">
                {/* Iter-234 — render grooming requirements as a list plus
                    any grooming reference images the client uploaded. */}
                {(det.grooming_list.length > 0 || det.grooming_images.length > 0 || det.grooming_requirements) ? (
                  <div className="space-y-2">
                    {det.grooming_list.length > 0 && (
                      <ul className="text-xs text-slate-700 space-y-1 pl-1" data-testid="grooming-requirements-list">
                        {det.grooming_list.map((item, i) => (
                          <li key={i} className="flex items-start gap-1.5">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {det.grooming_images.length > 0 && (
                      <div className="flex flex-wrap gap-2" data-testid="grooming-images">
                        {det.grooming_images.map((src, i) => (
                          <a
                            key={i}
                            href={src}
                            target="_blank"
                            rel="noreferrer"
                            className="block w-20 h-20 rounded-lg overflow-hidden border border-slate-200 bg-slate-50"
                          >
                            <img
                              src={src}
                              alt={`Grooming reference ${i + 1}`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </a>
                        ))}
                      </div>
                    )}
                    {det.grooming_list.length === 0 && det.grooming_requirements && (
                      <p className="text-xs text-slate-700 whitespace-pre-line">{det.grooming_requirements}</p>
                    )}
                  </div>
                ) : (
                  <NotProvided what="Grooming requirements" />
                )}
              </SectionRow>
              <SectionRow icon={Info} title="Event instructions" anchor="instructions">
                {(det.event_instructions || det.arrival_instructions || det.setup_instructions || det.entrance_instructions || det.onsite_directions) ? (
                  <div className="space-y-2">
                    {det.event_instructions && <p className="whitespace-pre-wrap">{det.event_instructions}</p>}
                    {det.arrival_instructions && (
                      <div data-testid="arrival-instructions"><span className="font-semibold text-slate-800">Arrival: </span>{det.arrival_instructions}</div>
                    )}
                    {det.entrance_instructions && (
                      <div data-testid="entrance-instructions"><span className="font-semibold text-slate-800">Entrance: </span>{det.entrance_instructions}</div>
                    )}
                    {det.onsite_directions && (
                      <div data-testid="onsite-directions"><span className="font-semibold text-slate-800">Onsite: </span>{det.onsite_directions}</div>
                    )}
                    {det.setup_instructions && (
                      <div data-testid="setup-instructions"><span className="font-semibold text-slate-800">Setup: </span>{det.setup_instructions}</div>
                    )}
                  </div>
                ) : null}
              </SectionRow>
              <SectionRow icon={Car} title="Parking" anchor="parking">
                {det.parking_info || <NotProvided what="Parking info" />}
              </SectionRow>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-3" data-testid="event-contact-card">
              <div className="text-[11px] uppercase font-bold text-slate-500 tracking-widest mb-2">Event contact</div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-cyan-100 grid place-items-center text-cyan-700 font-bold">
                  {(det.contact_name || booking.client_name || '?').charAt(0)}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-sm text-slate-900">{det.contact_name || booking.client_name || 'Site captain TBD'}</div>
                  <div className="text-xs text-slate-500">{det.contact_role || 'On-site contact'}</div>
                </div>
                {det.contact_phone && (
                  <a href={`tel:${det.contact_phone}`} className="w-9 h-9 rounded-full bg-cyan-50 text-cyan-700 grid place-items-center"><Phone className="w-4 h-4" /></a>
                )}
                <button onClick={() => navigate('/messages')} className="w-9 h-9 rounded-full bg-cyan-50 text-cyan-700 grid place-items-center">
                  <MessageSquare className="w-4 h-4" />
                </button>
              </div>
              {det.contact_instructions && (
                <p className="text-xs text-slate-700 whitespace-pre-wrap mt-2 pt-2 border-t border-slate-100" data-testid="contact-instructions">
                  {det.contact_instructions}
                </p>
              )}
            </div>

            {booking.staffing_manager_name && (
              <div className="bg-white rounded-xl border border-indigo-200 p-3" data-testid="staffing-manager-card">
                <div className="text-[11px] uppercase font-bold text-indigo-500 tracking-widest mb-2">iEvntür Staffing Manager</div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 grid place-items-center text-indigo-700 font-bold">
                    {booking.staffing_manager_name.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-sm text-slate-900" data-testid="staffing-manager-name">{booking.staffing_manager_name}</div>
                    <div className="text-xs text-slate-500">{booking.staffing_manager_title || 'Staffing Manager'}</div>
                  </div>
                  {booking.staffing_manager_phone && (
                    <a href={`tel:${String(booking.staffing_manager_phone).replace(/[^0-9+]/g, '')}`} className="w-9 h-9 rounded-full bg-indigo-50 text-indigo-700 grid place-items-center" data-testid="staffing-manager-call"><Phone className="w-4 h-4" /></a>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 mt-2">
                  For staffing questions, scheduling issues, releases, or iEvntür support, contact {booking.staffing_manager_name} at {booking.staffing_manager_phone}.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="docs-card">
                <div className="text-[10px] uppercase font-bold text-slate-500 mb-1.5">Required documents</div>
                <div className="space-y-1">
                  {det.required_documents.length > 0
                    ? det.required_documents.map((d, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />{d}
                        </div>
                      ))
                    : <NotProvided what="Required documents" />}
                  {det.certifications.map((c, i) => (
                    <div key={i} className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />Cert: {c}</div>
                  ))}
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="reporting-card">
                <div className="text-[10px] uppercase font-bold text-slate-500 mb-1.5">Reporting & documentation</div>
                <div className="text-xs text-slate-700 space-y-1">
                  {det.reporting_instructions && <p className="whitespace-pre-wrap">{det.reporting_instructions}</p>}
                  {det.recap_photo_min ? (
                    <div className="flex items-center gap-1.5" data-testid="recap-photo-min">
                      <ImageIcon className="w-3.5 h-3.5 text-cyan-600" /> At least {det.recap_photo_min} photo{det.recap_photo_min === 1 ? '' : 's'} required
                    </div>
                  ) : null}
                  {det.recap_required_shots.length > 0 && (
                    <ul className="list-disc list-inside" data-testid="recap-required-shots">
                      {det.recap_required_shots.map((s, i) => <li key={i}>{typeof s === 'string' ? s : (s?.label || s?.name || '')}</li>)}
                    </ul>
                  )}
                  {det.recap_required && (
                    <button onClick={() => setTab('recap')} className="text-cyan-700 font-semibold hover:underline" data-testid="goto-recap-from-reporting">
                      A post-event recap is required →
                    </button>
                  )}
                  {!det.reporting_instructions && !det.recap_photo_min && det.recap_required_shots.length === 0 && !det.recap_required && (
                    <NotProvided what="Reporting requirements" />
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {tab === 'tasks' && (
          <div data-testid="tasks-panel">
            <TalentTaskCard bookingId={id} onLoaded={setHasApprovedTasks} />
            {!hasApprovedTasks && (
            <>
            <div className="bg-cyan-50 border border-cyan-100 rounded-xl p-3 mb-4 flex items-center gap-3">
              <div className="relative w-12 h-12">
                <svg viewBox="0 0 36 36" className="w-12 h-12">
                  <circle cx="18" cy="18" r="16" stroke="#e2e8f0" strokeWidth="3" fill="none" />
                  <circle cx="18" cy="18" r="16" stroke="#0891b2" strokeWidth="3" strokeLinecap="round" fill="none"
                          strokeDasharray="100.5"
                          strokeDashoffset={100.5 - tasksPct}
                          transform="rotate(-90 18 18)" />
                </svg>
                <span className="absolute inset-0 grid place-items-center text-cyan-700 font-bold text-xs">{tasksPct}%</span>
              </div>
              <div>
                <div className="font-semibold text-sm text-slate-900">{tasksDone} of {tasks.length} tasks done</div>
                <p className="text-xs text-slate-600">Stay on track to avoid sent-home warnings.</p>
              </div>
            </div>
            <ol className="space-y-2">
              {tasks.map(t => (
                <li key={t.key}
                    className={`flex items-center gap-3 bg-white rounded-xl border p-3 ${t.done ? 'border-slate-200' : 'border-slate-200'}`}
                    data-testid={`task-${t.key}`}>
                  <span className={`w-7 h-7 rounded-full grid place-items-center shrink-0 ${
                    t.done ? 'bg-emerald-500 text-white shadow' : 'bg-slate-100 border border-slate-300 text-slate-500'
                  }`}>
                    {t.done ? <Check className="w-4 h-4" /> : <Circle className="w-3 h-3" />}
                  </span>
                  <span className={`text-sm flex-1 ${t.done ? 'line-through text-slate-500' : 'font-medium text-slate-800'}`}>
                    {t.label}
                  </span>
                </li>
              ))}
            </ol>
            </>
            )}
          </div>
        )}

        {/* Iter-232b — Upload Photos tab (was incorrectly routed to ClockInOut before). */}
        {tab === 'photos' && (
          <div data-testid="photos-panel" className="bg-white border border-slate-200 rounded-xl p-3">
            <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-cyan-700" /> Event Photos
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              Upload activation photos, shelf shots, setup photos, signage, and final-result images for this booking.
            </p>
            <JobPhotoUpload bookingId={booking.id} shiftId={booking.id} jobType="evntur" />
          </div>
        )}

        {/* Iter-232b — Submit Recap tab. Opens PostEventRecapModal. */}
        {tab === 'recap' && (
          <div data-testid="recap-panel" className="bg-white border border-slate-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4 text-cyan-700" /> Post-Event Recap
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              Submit your recap (rating + notes) so this booking can be marked complete and payout can move forward.
            </p>
            <Button
              onClick={() => setRecapOpen(true)}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="open-recap-btn"
            >
              <ClipboardCheck className="w-4 h-4 mr-1.5" /> {booking.recap_submitted_at ? 'View / Re-submit Recap' : 'Start Recap'}
            </Button>
            {booking.recap_submitted_at && (
              <p className="text-xs text-emerald-700 mt-3 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" /> Recap submitted {formatDate(booking.recap_submitted_at)}
              </p>
            )}
          </div>
        )}

        {/* Iter-232b — Reimbursements tab (uses existing TalentExpensesPanel). */}
        {tab === 'reimbursements' && (
          <div data-testid="reimbursements-panel" className="bg-white border border-slate-200 rounded-xl p-3">
            <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
              <Receipt className="w-4 h-4 text-cyan-700" /> Reimbursements
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              Submit receipts, parking, supplies, or other approved expenses tied to this booking for admin review.
            </p>
            <TalentExpensesPanel bookingId={booking.id} bookingStatus={booking.status} />
          </div>
        )}

        {tab === 'shifts' && (
          <div className="bg-white border border-slate-200 rounded-xl p-4 text-sm text-slate-600" data-testid="shifts-panel">
            This booking row represents a single shift. To see the full multi-day series,{' '}
            <button onClick={() => navigate('/talent/bookings-v2')} className="text-cyan-700 font-semibold hover:underline">
              go back to My Bookings
            </button>{' '}and expand the event card.
          </div>
        )}
      </div>

      {/* Sticky bottom CTA */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 px-4 py-3 flex gap-2 z-40">
        <Button onClick={() => navigate(`/talent/clock?booking=${booking.id}`)}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                data-testid="primary-bottom-cta">
          <Play className="w-3.5 h-3.5 mr-1.5" /> Check In
        </Button>
        <Button variant="outline" onClick={() => setActionsOpen(true)} data-testid="open-actions">
          <LayoutGrid className="w-3.5 h-3.5 mr-1.5" /> Actions
        </Button>
      </div>
      </>
      )}

      <BookingActionsSheet
        open={actionsOpen}
        onClose={() => setActionsOpen(false)}
        group={{
          client_name: booking.client_name,
          event_title: booking.event_title,
          role: booking.gig_role_name,
          venue: booking.venue_name,
          city: booking.city,
          state: booking.state,
          hourly_rate: booking.hourly_rate,
          shifts: [booking],
          total_shifts: 1,
        }}
        shift={booking}
        onChange={() => navigate('/talent/bookings-v2')}
      />

      {/* Iter-232b — Post-event recap modal (auto-opens when ?tab=recap). */}
      <PostEventRecapModal
        isOpen={recapOpen}
        onClose={() => setRecapOpen(false)}
        booking={booking}
        onRecapSubmitted={() => {
          setRecapOpen(false);
          toast.success('Recap submitted');
          // Refresh booking so badge/status updates.
          talentAPI.getBookings()
            .then(r => setBooking((r.data || []).find(b => b.id === id) || booking))
            .catch(() => {});
        }}
      />
    </div>
  );
};

export default BookingDetailsV2;
